// TenantAdminAcceptPage — public landing for tenant-admin invitation links.
//
// Backend surface (2026-05-11 design):
//
//   GET  /api/v1/tenant-admin-invitations/{token}        (PUBLIC) → preview
//   POST /api/v1/tenant-admin-invitations/{token}/accept (PUBLIC) → activate
//
// The page is intentionally NOT wrapped in <RedirectIfAuthenticated /> —
// an already-logged-in user might be accepting an invitation for a
// different email and we want to surface that, not bounce to /dashboard.
//
// Error messages are sanitized: no internal-service names ("aegis",
// "kratos", "schema validation"…) ever reach the user.

import type { components } from '@intelliauth/api-client-v2-cp/types'
import {
  Alert,
  AmbientBackground,
  Button,
  buttonVariants,
  Card,
  createLogger,
  Input,
  IntelliAuthLogo,
  Label,
} from '@intelliauth/ui'
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core'
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common'
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en'
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Clock,
  Eye,
  EyeOff,
  Loader2,
  XCircle,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CpV2ApiError, cpClient } from '@/lib/api/cpClient'

// Configure zxcvbn once at module load — same pattern as RegisterPage so
// the dictionaries + graph model are paid for once per tab.
zxcvbnOptions.setOptions({
  translations: zxcvbnEnPackage.translations,
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
  },
})

const log = createLogger({ component: 'TenantAdminAcceptPage' })

type Preview = components['schemas']['TenantAdminInvitationPreview']

type ViewState =
  | { kind: 'loading' }
  | { kind: 'form'; preview: Preview }
  | { kind: 'accepted'; preview?: Preview }
  | { kind: 'expired'; preview?: Preview }
  | { kind: 'revoked'; preview?: Preview }
  | { kind: 'not_found' }
  | { kind: 'tenant_unavailable' }
  | { kind: 'error'; message: string }

// Generic, user-facing fallbacks. Never expose internal service names.
const ERR_GENERIC = 'Something went wrong. Please try again.'
const ERR_UNAVAILABLE = 'Service temporarily unavailable. Please try again.'
const ERR_PASSWORD_REJECTED = "Password doesn't meet the requirements."
const ERR_EMAIL_IN_USE =
  'An account already exists for this email. Ask the org owner to grant access via tenant settings.'

// Field-error shape that the CP envelope nests under `error.details`.
interface FieldError {
  field?: string
  message?: string
}

function pickFieldError(err: CpV2ApiError, field: string): string | null {
  const details = (err.details ?? {}) as { field_errors?: FieldError[] }
  const match = (details.field_errors ?? []).find((fe) => fe.field === field)
  return match?.message ?? null
}

export default function TenantAdminAcceptPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [view, setView] = useState<ViewState>({ kind: 'loading' })
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [passwordFieldError, setPasswordFieldError] = useState<string | null>(null)

  // ── Preview fetch ────────────────────────────────────────────
  const fetchPreview = useCallback(async (): Promise<Preview | null> => {
    try {
      const env = await cpClient().get('/api/v1/tenant-admin-invitations/{token}', {
        path: { token },
      })
      const data = (env as { data: Preview }).data
      return data ?? null
    } catch (err) {
      if (err instanceof CpV2ApiError) {
        if (err.status === 404) {
          setView({ kind: 'not_found' })
          return null
        }
        if (err.status === 410) {
          setView({ kind: 'tenant_unavailable' })
          return null
        }
      }
      setView({ kind: 'error', message: ERR_GENERIC })
      return null
    }
  }, [token])

  useEffect(() => {
    if (!token) {
      setView({ kind: 'error', message: 'Invitation link is missing.' })
      return
    }
    let cancelled = false
    fetchPreview().then((preview) => {
      if (cancelled || !preview) return
      switch (preview.status) {
        case 'pending':
          setView({ kind: 'form', preview })
          break
        case 'accepted':
          setView({ kind: 'accepted', preview })
          break
        case 'expired':
          setView({ kind: 'expired', preview })
          break
        case 'revoked':
          setView({ kind: 'revoked', preview })
          break
        default:
          setView({ kind: 'error', message: ERR_GENERIC })
      }
    })
    return () => {
      cancelled = true
    }
  }, [token, fetchPreview])

  // ── Password strength (mirrors RegisterPage exactly) ─────────
  const passwordChecks = useMemo(() => {
    const p = password
    return {
      length: p.length >= 8,
      upper: /[A-Z]/.test(p),
      lower: /[a-z]/.test(p),
      digit: /[0-9]/.test(p),
      special: /[^A-Za-z0-9]/.test(p),
    }
  }, [password])

  const zxcvbnResult = useMemo(() => {
    if (!password) return { score: 0, feedback: null as string | null }
    const r = zxcvbn(password)
    return {
      score: r.score,
      feedback: r.feedback.warning || r.feedback.suggestions[0] || null,
    }
  }, [password])

  const passwordStrength = useMemo(() => {
    if (!password) return { score: 0, label: '', color: '' }
    switch (zxcvbnResult.score) {
      case 0:
        return { score: 0, label: 'Very weak', color: 'bg-red-500' }
      case 1:
        return { score: 1, label: 'Weak', color: 'bg-red-400' }
      case 2:
        return { score: 2, label: 'Fair', color: 'bg-yellow-500' }
      case 3:
        return { score: 3, label: 'Good', color: 'bg-emerald-400' }
      default:
        return { score: 4, label: 'Strong', color: 'bg-emerald-500' }
    }
  }, [password, zxcvbnResult.score])

  const allPasswordCriteriaMet =
    passwordChecks.length &&
    passwordChecks.upper &&
    passwordChecks.lower &&
    passwordChecks.digit &&
    passwordChecks.special &&
    zxcvbnResult.score >= 2

  // ── Submit ───────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (view.kind !== 'form' || !allPasswordCriteriaMet || submitting) return

    setSubmitting(true)
    setSubmitError(null)
    setPasswordFieldError(null)
    log.info('Submitting tenant-admin acceptance', { tenant: view.preview.tenant_slug })

    try {
      const env = await cpClient().post('/api/v1/tenant-admin-invitations/{token}/accept', {
        path: { token },
        body: {
          password,
          ...(fullName.trim() ? { full_name: fullName.trim() } : {}),
        },
      })
      const data = (env as { data: components['schemas']['TenantAdminAcceptResponse'] }).data
      const redirect = data?.redirect_to
      log.info('Tenant-admin acceptance succeeded', { hasRedirect: Boolean(redirect) })
      window.location.href = redirect && redirect.length > 0 ? redirect : '/login'
    } catch (err) {
      if (err instanceof CpV2ApiError) {
        log.debug('Tenant-admin acceptance error', { status: err.status, code: err.code })
        if (err.status === 422) {
          const fe = pickFieldError(err, 'password')
          setPasswordFieldError(fe ?? ERR_PASSWORD_REJECTED)
          setSubmitError(null)
        } else if (err.status === 409) {
          setView({ kind: 'accepted', preview: view.preview })
          setSubmitError(ERR_EMAIL_IN_USE)
        } else if (err.status === 410) {
          // Re-fetch to determine the current state (expired vs revoked
          // vs accepted vs tenant_unavailable).
          const refreshed = await fetchPreview()
          if (refreshed) {
            switch (refreshed.status) {
              case 'accepted':
                setView({ kind: 'accepted', preview: refreshed })
                break
              case 'expired':
                setView({ kind: 'expired', preview: refreshed })
                break
              case 'revoked':
                setView({ kind: 'revoked', preview: refreshed })
                break
              default:
                setView({ kind: 'tenant_unavailable' })
            }
          }
        } else if (err.status === 503) {
          setSubmitError(ERR_UNAVAILABLE)
        } else {
          setSubmitError(ERR_GENERIC)
        }
      } else {
        setSubmitError(ERR_GENERIC)
      }
    } finally {
      setSubmitting(false)
    }
  }

  // ── Render ───────────────────────────────────────────────────
  if (view.kind === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <AmbientBackground variant="full" />
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (view.kind === 'not_found') {
    return (
      <StatusCard
        tone="error"
        icon={XCircle}
        title="Invitation invalid"
        description="This invitation link is invalid or has been removed."
        action={
          <Link to="/login" className={buttonVariants({ variant: 'primary' })}>
            Go to sign in
          </Link>
        }
      />
    )
  }

  if (view.kind === 'tenant_unavailable') {
    return (
      <StatusCard
        tone="error"
        icon={XCircle}
        title="Tenant no longer available"
        description="The tenant for this invitation is no longer available. Ask the org owner for help."
        action={
          <Link to="/login" className={buttonVariants({ variant: 'primary' })}>
            Go to sign in
          </Link>
        }
      />
    )
  }

  if (view.kind === 'expired') {
    return (
      <StatusCard
        tone="warning"
        icon={Clock}
        title="Invitation expired"
        description={
          view.preview ? (
            <>
              This invitation to <strong>{view.preview.tenant_name}</strong> has expired. Ask the
              org owner to send a new one.
            </>
          ) : (
            'This invitation has expired. Ask the org owner to send a new one.'
          )
        }
        action={
          <Link to="/login" className={buttonVariants({ variant: 'primary' })}>
            Go to sign in
          </Link>
        }
      />
    )
  }

  if (view.kind === 'revoked') {
    return (
      <StatusCard
        tone="error"
        icon={XCircle}
        title="Invitation revoked"
        description={
          view.preview ? (
            <>
              This invitation to <strong>{view.preview.tenant_name}</strong> has been revoked.
            </>
          ) : (
            'This invitation has been revoked.'
          )
        }
        action={
          <Link to="/login" className={buttonVariants({ variant: 'primary' })}>
            Go to sign in
          </Link>
        }
      />
    )
  }

  if (view.kind === 'accepted') {
    return (
      <StatusCard
        tone="success"
        icon={CheckCircle2}
        title="Already activated"
        description={
          view.preview ? (
            <>
              The admin account for <strong>{view.preview.tenant_name}</strong> has already been
              activated. Sign in to continue.
            </>
          ) : (
            'This admin account has already been activated. Sign in to continue.'
          )
        }
        action={
          <Link to="/login" className={buttonVariants({ variant: 'primary' })}>
            Sign in
          </Link>
        }
      />
    )
  }

  if (view.kind === 'error') {
    return (
      <StatusCard
        tone="error"
        icon={AlertCircle}
        title="Something went wrong"
        description={view.message}
        action={
          <Link to="/login" className={buttonVariants({ variant: 'primary' })}>
            Go to sign in
          </Link>
        }
      />
    )
  }

  // view.kind === 'form'
  const preview = view.preview
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <AmbientBackground variant="full" />
      <div className="w-full max-w-[500px] animate-fade-in">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex justify-center">
            <IntelliAuthLogo size="lg" />
          </Link>
        </div>

        <Card className="p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-foreground">Activate your admin account</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              You've been invited to administer{' '}
              <strong className="text-foreground">{preview.tenant_name}</strong>
              {preview.org_name ? (
                <>
                  {' '}
                  in <strong className="text-foreground">{preview.org_name}</strong>
                </>
              ) : null}
              . Set a password to finish setting up your account.
            </p>
          </div>

          {submitError && (
            <Alert tone="error" className="mb-6">
              {submitError}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="mb-1.5">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={preview.email}
                readOnly
                className="h-10 bg-foreground/5"
              />
              <p className="mt-1 text-xs text-muted-foreground/70">
                This invitation was sent to this address; it cannot be changed.
              </p>
            </div>

            <div>
              <Label htmlFor="fullName" className="mb-1.5">
                Full name <span className="text-muted-foreground/60 font-normal">(optional)</span>
              </Label>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="h-10"
                placeholder="Alex Chen"
                autoComplete="name"
              />
            </div>

            <div>
              <Label htmlFor="password" className="mb-1.5">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    if (passwordFieldError) setPasswordFieldError(null)
                  }}
                  className={`h-10 pr-10 ${passwordFieldError ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {passwordFieldError && (
                <p className="mt-1.5 text-sm text-red-400">{passwordFieldError}</p>
              )}

              {password && (
                <div className="mt-3">
                  <div className="flex gap-1 mb-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full ${
                          i <= passwordStrength.score ? passwordStrength.color : 'bg-foreground/10'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Password strength: <span className="font-medium">{passwordStrength.label}</span>
                  </p>
                  <div className="mt-2 space-y-1">
                    {[
                      { test: passwordChecks.length, label: '8+ characters' },
                      { test: passwordChecks.upper, label: 'Uppercase letter' },
                      { test: passwordChecks.lower, label: 'Lowercase letter' },
                      { test: passwordChecks.digit, label: 'Number' },
                      { test: passwordChecks.special, label: 'Special character' },
                    ].map((req) => (
                      <div key={req.label} className="flex items-center gap-2 text-xs">
                        <div
                          className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${req.test ? 'bg-emerald-500' : 'bg-foreground/10'}`}
                        >
                          {req.test && <Check className="h-2.5 w-2.5 text-white" />}
                        </div>
                        <span
                          className={
                            req.test ? 'text-muted-foreground' : 'text-muted-foreground/60'
                          }
                        >
                          {req.label}
                        </span>
                      </div>
                    ))}
                  </div>
                  {zxcvbnResult.feedback && zxcvbnResult.score < 2 && (
                    <p className="mt-2 text-xs text-amber-500">{zxcvbnResult.feedback}</p>
                  )}
                </div>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              disabled={!allPasswordCriteriaMet || submitting}
              loading={submitting}
              className="w-full h-10"
            >
              {submitting ? 'Activating...' : 'Activate account'}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-primary hover:text-primary/80 transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  )
}

// Tone classes follow the existing palette used by other status cards in
// this app (InviteAcceptPage).
const STATUS_TONE: Record<'error' | 'warning' | 'success', { bg: string; fg: string }> = {
  error: { bg: 'bg-destructive/10', fg: 'text-destructive' },
  warning: { bg: 'bg-ia-warning-bg', fg: 'text-ia-warning' },
  success: { bg: 'bg-ia-success-bg', fg: 'text-ia-success' },
}

function StatusCard({
  tone,
  icon: Icon,
  title,
  description,
  action,
}: {
  tone: 'error' | 'warning' | 'success'
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: React.ReactNode
  action: React.ReactNode
}) {
  const { bg, fg } = STATUS_TONE[tone]
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <AmbientBackground variant="full" />
      <div className="max-w-md w-full text-center relative">
        <div
          className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6 ${bg}`}
        >
          <Icon className={`h-8 w-8 ${fg}`} />
        </div>
        <h1 className="text-2xl font-semibold text-foreground mb-2">{title}</h1>
        <p className="text-muted-foreground mb-8">{description}</p>
        {action}
      </div>
    </div>
  )
}
